// =====================================================================
// planVisits — single source of truth for "what does the patient
// experience visit-by-visit" across the platform.
//
// See REPORT_VISIT_AS_SCHEDULING_UNIT.md for the locked architecture.
// `treatment_plan_items` is the financial unit. `procedure_visits` is
// the scheduling unit. This module bridges the two: any consumer
// (PDF, work-draft phase view, future calendar, future scheduler RPC
// callers) reads the same expanded shape.
//
// Key idea: every plan item has ≥1 procedure_visits row after the
// universal-visits migration (20260614000001). Multi-visit items
// have N rows. Single-visit items have 1. Display layers iterate
// visits, not items, when answering scheduling-shape questions.
//
// The fee_centavos field is a math allocation helper: multi-visit
// rows anchor the amount once so totals do not double-count. Human-
// facing layers should render fee_display, which says "Included" or
// "case total" instead of exposing raw 0-value clinical stages.
// =====================================================================

import { treatmentPlanVisitFeeDisplay, treatmentPlanVisitFeeDisplayForVisit } from "./treatmentPlanFeeDisplay";

/**
 * Expand a treatment plan into visit-shaped rows.
 *
 * Each row carries the visit's identity + the parent item's identity,
 * with fee anchored to the final visit. Single-visit items emit one
 * row; multi-visit items emit N rows (one per procedure_visit).
 *
 * Legacy items with no procedure_visits at all (pre-migration data
 * that hasn't been backfilled yet) get a synthetic row so consumers
 * never see "0 visits for this item" — `is_synthetic: true` lets the
 * caller flag this as a hygiene issue.
 *
 * @param {object} plan — { items: [{ id, fee_centavos, status,
 *   procedure_visits: [...], ... }] }
 * @returns {Array<VisitRow>}
 *
 * @typedef {object} VisitRow
 * @property {'visit'} kind
 * @property {string} item_id
 * @property {object} item — the parent treatment_plan_item
 * @property {?string} visit_id — null for synthetic rows
 * @property {number} sequence
 * @property {string} label
 * @property {?number} chair_min
 * @property {?number} lab_gap_days
 * @property {?number} phase_number
 * @property {boolean} is_final_visit — fee anchors here
 * @property {boolean} is_synthetic — true for legacy items lacking visits
 * @property {number} fee_centavos — only non-zero on is_final_visit
 * @property {object} fee_display — patient-facing fee presentation
 * @property {?string} scheduled_at
 * @property {?string} completed_at
 */
export function expandPlanToVisits(plan) {
  const items = plan?.items || [];
  const out = [];
  for (const it of items) {
    if (it.status === "Declined" || it.status === "Cancelled") continue;

    const visits = Array.isArray(it.procedure_visits) ? it.procedure_visits : [];

    if (visits.length === 0) {
      // Legacy item lacking procedure_visits. Synthesize one so
      // consumers always see ≥1 visit. Once the universal-visits
      // migration is applied, this branch should never fire on
      // current data.
      out.push({
        kind: "visit",
        item_id: it.id,
        item: it,
        visit_id: null,
        sequence: 1,
        label: it.display_label || it.procedure_name || "Procedure",
        chair_min: null,
        lab_gap_days: null,
        phase_number: it.phase_number ?? null,
        is_final_visit: true,
        is_synthetic: true,
        fee_centavos: Number(it.fee_centavos || 0),
        fee_display: treatmentPlanVisitFeeDisplay(it, {
          isStageEntry: false,
          hasStages: false,
        }),
        scheduled_at: null,
        completed_at: it.completed_at ?? null,
      });
      continue;
    }

    const sorted   = [...visits].sort((a, b) => (a.sequence || 0) - (b.sequence || 0));
    const finalSeq = sorted[sorted.length - 1].sequence;

    for (const v of sorted) {
      const isFinal = v.sequence === finalSeq;
      out.push({
        kind: "visit",
        item_id: it.id,
        item: it,
        visit_id: v.id,
        sequence: Number(v.sequence || 0),
        label: v.template_label || it.display_label || it.procedure_name || `Visit ${v.sequence}`,
        chair_min: v.chair_min ?? null,
        lab_gap_days: v.lab_gap_days ?? null,
        phase_number: v.phase_number ?? it.phase_number ?? null,
        is_final_visit: isFinal,
        is_synthetic: false,
        // Fee anchors to the final visit. Sums across visits =
        // item.fee_centavos exactly. Patient-facing layers render
        // fee_display instead of exposing raw zeroes.
        fee_centavos: isFinal ? Number(it.fee_centavos || 0) : 0,
        fee_display: treatmentPlanVisitFeeDisplay(it, {
          isStageEntry: sorted.length >= 2,
          isFinalStageEntry: isFinal,
          hasStages: sorted.length >= 2,
        }),
        scheduled_at: v.scheduled_at ?? null,
        completed_at: v.completed_at ?? null,
      });
    }
  }
  return out;
}

/**
 * Group visits by their phase_number. Visits without a phase land
 * under the special "null" key.
 *
 * @returns {Map<number|null, VisitRow[]>}
 */
export function groupVisitsByPhase(visits) {
  const map = new Map();
  for (const v of visits) {
    const p = v.phase_number;
    if (!map.has(p)) map.set(p, []);
    map.get(p).push(v);
  }
  return map;
}

/**
 * Sum raw chair_min across all visits in a phase. Callers that display an
 * appointment duration must add chair setup once per phase when applicable.
 */
export function phaseChairMinutes(visits, phaseNumber) {
  return visits
    .filter((v) => v.phase_number === phaseNumber)
    .reduce((sum, v) => sum + (Number(v.chair_min) || 0), 0);
}

/**
 * Detect whether a plan item's visits actually spread across multiple
 * phases — only then do we want to render it as N visit rows in the
 * phase view. If all visits share one phase, the legacy collapsed
 * "one row + visit-count subtitle" rendering stays (less density,
 * same information).
 */
export function itemSpansMultiplePhases(item) {
  const visits = Array.isArray(item?.procedure_visits) ? item.procedure_visits : [];
  if (visits.length < 2) return false;
  const phases = new Set();
  for (const v of visits) {
    phases.add(v.phase_number ?? item.phase_number ?? null);
  }
  return phases.size > 1;
}

/**
 * Total chair time required to book the given plan items together in
 * one appointment. For each item, picks the next unscheduled
 * procedure_visit (smallest sequence with no scheduled_at) and sums its
 * chair_min. Adds chair-setup overhead once if any of the picked visits
 * use chair setup.
 *
 * Returns 0 when no items carry chair_min data — callers should fall
 * back to a sensible default for legacy/pre-universal-visits plans.
 *
 * @param {Array} items — plan items, each shaped like
 *   { id, procedure_id, procedure_visits: [{ chair_min, sequence,
 *     scheduled_at, ... }, ...] }
 * @param {object} [opts]
 * @param {number} [opts.chairSetupMinutes=0] — clinic-wide setup overhead
 *   (typically clinic_settings.chair_setup_minutes, defaults to 15 in
 *   the DB).
 * @param {Map<string, boolean>|null} [opts.procedureUsesSetup=null] —
 *   per-procedure flag map (procedure_id → uses_chair_setup). When null,
 *   assumes every item uses setup, which matches the DB default.
 * @returns {number}
 */
export function sumPlanItemsChairMinutes(items, opts = {}) {
  const { chairSetupMinutes = 0, procedureUsesSetup = null } = opts;
  let totalRaw = 0;
  let anyUsesSetup = false;
  let hadChairMinData = false;
  for (const it of items || []) {
    const visits = Array.isArray(it?.procedure_visits) ? it.procedure_visits : [];
    if (visits.length === 0) continue;
    const unscheduled = visits
      .filter((v) => !v?.scheduled_at)
      .sort((a, b) => (Number(a?.sequence) || 0) - (Number(b?.sequence) || 0));
    const nextVisit = unscheduled[0] || visits[0];
    const chairMin = Number(nextVisit?.chair_min) || 0;
    if (chairMin > 0) {
      totalRaw += chairMin;
      hadChairMinData = true;
    }
    if (procedureUsesSetup === null) {
      anyUsesSetup = true;
    } else if (procedureUsesSetup.get?.(it?.procedure_id) !== false) {
      anyUsesSetup = true;
    }
  }
  if (!hadChairMinData) return 0;
  const setup = anyUsesSetup ? Number(chairSetupMinutes) || 0 : 0;
  return totalRaw + setup;
}

/**
 * Total raw chair time for concrete procedure_visit rows.
 *
 * Use this when the booking unit is already a visit/stage, not the
 * parent treatment_plan_item. This intentionally does not add chair
 * setup, because the appointment board lane total is the sum of the
 * actual stage durations the receptionist is booking.
 */
export function sumProcedureVisitChairMinutes(visits) {
  let total = 0;
  for (const visit of visits || []) {
    total += Number(visit?.chair_min || 0);
  }
  return total;
}

/**
 * The dentist's typed per-appointment duration for a phase, stored on
 * treatment_plans.phase_meta[<phase>].appointment_duration_min by
 * set_plan_phase_duration / set_phase_appointment_duration. Returns 0
 * when no explicit duration was typed for that phase.
 */
export function plannedPhaseAppointmentMinutes(phaseMeta, phaseNumber) {
  if (!phaseMeta || phaseNumber == null) return 0;
  const entry = phaseMeta[String(phaseNumber)];
  const minutes = Number(entry?.appointment_duration_min);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
}

/**
 * How long the calendar block should be when these visit/stage rows are
 * booked into one appointment.
 *
 * When the receptionist is booking a single appointment phase and the
 * dentist typed an explicit duration for that phase, that typed value is
 * the source of truth for the calendar block (e.g. "book Appointment 1 →
 * 15 min" even if the raw chair time sums higher). If the booking spans
 * multiple phases, or no duration was typed, fall back to the summed
 * chair minutes of the selected stages.
 */
export function resolveBookingDurationMinutes(visits, phaseMeta) {
  const phases = new Set((visits || []).map((v) => Number(v?.phase_number) || 1));
  if (phases.size === 1) {
    const [only] = [...phases];
    const typed = plannedPhaseAppointmentMinutes(phaseMeta, only);
    if (typed > 0) return typed;
  }
  return sumProcedureVisitChairMinutes(visits);
}

/**
 * Effective per-appointment durations, matching what the Tx Plan board
 * shows so the PDF and the board never disagree. Precedence per phase:
 *   1. booked appointment length (procedure_visits.appointment_duration_min)
 *   2. the dentist's typed duration (phase_meta[phase].appointment_duration_min)
 *   3. raw chair minutes + one chair-setup buffer for the appointment
 *
 * @param {Array} items — plan items with procedure_visits
 * @param {object} phaseMeta — plan.phase_meta
 * @param {object} [opts]
 * @param {number} [opts.chairSetupMinutes=15]
 * @param {Map<string,boolean>|null} [opts.procedureUsesSetup=null] — procedure_id → uses setup
 * @returns {Object<string, number>} phase number (string) → minutes
 */
export function computePhaseDurations(items, phaseMeta = {}, opts = {}) {
  const chairSetupMinutes = Number(opts.chairSetupMinutes ?? 15) || 0;
  const procedureUsesSetup = opts.procedureUsesSetup || null;
  const byPhase = {};
  for (const it of items || []) {
    if (it?.status === "Declined" || it?.status === "Cancelled") continue;
    for (const v of (Array.isArray(it?.procedure_visits) ? it.procedure_visits : [])) {
      const n = Number(v?.phase_number ?? it?.phase_number);
      if (!Number.isFinite(n) || n < 1) continue;
      if (!byPhase[n]) byPhase[n] = { raw: 0, usesSetup: false, booked: null };
      byPhase[n].raw += Number(v?.chair_min || 0);
      const uses = procedureUsesSetup ? (procedureUsesSetup.get?.(it.procedure_id) !== false) : true;
      byPhase[n].usesSetup = byPhase[n].usesSetup || uses;
      const booked = Number(v?.appointment_duration_min);
      if (Number.isFinite(booked) && booked > 0) {
        byPhase[n].booked = byPhase[n].booked == null ? booked : Math.max(byPhase[n].booked, booked);
      }
    }
  }
  const out = {};
  for (const [phase, m] of Object.entries(byPhase)) {
    const typed = Number(phaseMeta?.[String(phase)]?.appointment_duration_min);
    if (m.booked != null) out[phase] = m.booked;
    else if (Number.isFinite(typed) && typed > 0) out[phase] = typed;
    else out[phase] = m.raw + (m.usesSetup ? chairSetupMinutes : 0);
  }
  return out;
}

export function isBookablePlanItemStatus(status) {
  const normalized = String(status || "").trim().toLowerCase();
  return normalized !== "declined" && normalized !== "cancelled";
}

export function buildBookableVisitRows(items = []) {
  const rows = [];
  for (const item of items || []) {
    if (!isBookablePlanItemStatus(item?.status)) continue;
    const visits = Array.isArray(item?.procedure_visits) ? item.procedure_visits : [];
    const sortedVisits = [...visits].sort((a, b) => (Number(a?.sequence) || 0) - (Number(b?.sequence) || 0));
    for (const visit of visits) {
      if (visit?.appointment_id || visit?.completed_at) continue;
      if (visit?.outcome !== null && visit?.outcome !== undefined) continue;
      const sequence = Number(visit?.sequence) || 1;
      const subLabel = visit?.template_label
        ? ` · ${visit.template_label}`
        : (sequence > 1 ? ` · Visit ${sequence}` : "");
      rows.push({
        id:                 visit.id,
        visit_id:           visit.id,
        item_id:            item.id,
        sequence,
        phase_number:       Number(visit?.phase_number ?? item?.phase_number ?? 1) || 1,
        template_label:     visit?.template_label || null,
        chair_min:          Number(visit?.chair_min || 0),
        lab_gap_days:       Number(visit?.lab_gap_days || item?.lab_gap_days || 0),
        procedure_id:       item.procedure_id || null,
        procedure_name:     item.procedure_name || "Procedure",
        tooth_number:       item.tooth_number ?? null,
        surface:            item.surface || null,
        procedure_category: item.procedure_category || null,
        display_label:      `${item.procedure_name || "Procedure"}${subLabel}`,
        fee_centavos:       sequence === 1 ? Number(item.fee_centavos || 0) : 0,
        fee_display:        treatmentPlanVisitFeeDisplayForVisit(
          { ...item, procedure_visits: sortedVisits },
          visit,
        ),
      });
    }
  }
  return rows.sort((a, b) => {
    if (a.phase_number !== b.phase_number) return a.phase_number - b.phase_number;
    if (a.sequence !== b.sequence) return a.sequence - b.sequence;
    return String(a.display_label).localeCompare(String(b.display_label));
  });
}
